import c from 'classnames';
import arrayMutators from 'final-form-arrays';
import { useCallback, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useToasts } from 'react-toast-notifications';
import Button from '../../components/Button';
import Error from '../../components/Error';
import { FormSection, FormSectionBody, FormSectionHeader, StyledForm } from '../../components/Form';
import Header from '../../components/Header';
import IconCoupons from '../../components/icons/IconCoupons';
import { useAxios } from '../../utils/hooks';
import VALIDATORS, { composeValidators } from '../../utils/validators';

const MODES: FormData['mode'][] = ['Live', 'Test'];

interface FormData {
    mode: 'Live' | 'Test';
    noCoupons: number;
    description: string;
    percentageOff: number;
    noMonths: number;
    products?: string[];
}

interface GeneratedCoupon {
    code: string;
    stripeCouponId: string;
    description: string;
}

export default function Coupons() {
    const axios = useAxios();
    const { addToast } = useToasts();
    const [coupons, setCoupons] = useState<{ time: Date; coupons: GeneratedCoupon[] }[]>([]);

    const handleGenerateCoupons = useCallback(
        (data: FormData) => {
            if (!axios || !axios.current) {
                return Promise.resolve();
            }
            return axios.current?.post<GeneratedCoupon[]>('/coupons/generate', data).then((res) => {
                setCoupons([{ time: new Date(), coupons: res.data }, ...coupons]);
                addToast('Coupons generated', { appearance: 'success' });
            });
        },
        [addToast, axios, coupons]
    );

    return (
        <div className="w-full">
            <Header>
                <IconCoupons className="inline w-6 mr-3" /> Generate Stripe Coupons
            </Header>
            <Form<FormData>
                onSubmit={handleGenerateCoupons}
                initialValues={{ mode: 'Test' }}
                mutators={{
                    ...(arrayMutators as any),
                }}
            >
                {({ handleSubmit, submitting, invalid, values }) => (
                    <StyledForm onSubmit={handleSubmit} className="w-full mt-4 shadow">
                        <FormSection>
                            <FormSectionHeader>Generate Coupons</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Stripe Mode</label>
                                    <div className="flex justify-start space-x-2">
                                        {MODES.map((mode) => (
                                            <div className="form-check form-check-inline">
                                                <Field name="mode" type="radio" value={mode}>
                                                    {(props) => (
                                                        <label>
                                                            <input {...props.input} className="form-radio mr-2" />
                                                            <span
                                                                className={c(
                                                                    'form-check-label inline-block text-gray-800',
                                                                    {
                                                                        'text-green-500': mode === 'Live',
                                                                    }
                                                                )}
                                                            >
                                                                {mode}
                                                            </span>
                                                        </label>
                                                    )}
                                                </Field>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Number of coupons</label>
                                    <Field
                                        name="noCoupons"
                                        component="input"
                                        type="number"
                                        validate={composeValidators(
                                            VALIDATORS.required,
                                            VALIDATORS.min(0),
                                            VALIDATORS.max(5000)
                                        )}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input
                                                    {...input}
                                                    placeholder="1000"
                                                    className="input block w-1/2 md:w-1/4"
                                                />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Description</label>
                                    <Field
                                        name="description"
                                        component="input"
                                        type="text"
                                        validate={VALIDATORS.required}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input {...input} placeholder="My LTD" className="input w-full" />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Discount percentage</label>
                                    <Field
                                        name="percentageOff"
                                        component="input"
                                        type="number"
                                        validate={composeValidators(
                                            VALIDATORS.required,
                                            VALIDATORS.min(0),
                                            VALIDATORS.max(100)
                                        )}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <div className="w-full">
                                                    <input
                                                        {...input}
                                                        placeholder="100"
                                                        className="input w-1/2 md:w-1/4"
                                                    />
                                                    %
                                                </div>
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Discount length</label>
                                    <Field name="noMonths" component="input" type="number" validate={VALIDATORS.min(0)}>
                                        {({ input, meta }) => (
                                            <>
                                                <div className="w-full">
                                                    <input
                                                        {...input}
                                                        placeholder="12"
                                                        className="input w-1/2 md:w-1/4"
                                                    />{' '}
                                                    months
                                                </div>
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="flex">
                                    {values.noCoupons > 500 && (
                                        <div className="text-sm font-light">
                                            It can take a couple of minutes to generate so many codes. Please be
                                            patient.
                                        </div>
                                    )}
                                    <Button
                                        type="submit"
                                        kind="tertiary"
                                        className="ml-auto"
                                        state={submitting ? 'SAVING' : 'READY'}
                                        disabled={invalid}
                                    >
                                        Generate
                                    </Button>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                    </StyledForm>
                )}
            </Form>
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mx-12 mt-8">
                <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Coupons</h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">Generated coupons appear here.</p>
                </div>
                {coupons.length > 0 && (
                    <>
                        <div className="border-t border-gray-200"></div>
                        <div className="px-4 py-5">
                            <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
                                {coupons.map((coupon, idx) => (
                                    <li className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" key={idx}>
                                        <div className="w-0 flex-1 flex items-center">
                                            <svg
                                                className="shrink-0 h-5 w-5 text-gray-400"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            <span className="ml-2 flex-1 w-0 truncate">
                                                {' '}
                                                {coupon.time.toISOString()}{' '}
                                            </span>
                                        </div>
                                        <div className="ml-4 shrink-0">
                                            <a
                                                download={`pricewell-coupons-${coupon.time.toISOString()}.csv`}
                                                href={`data:text/csv;charset=utf-8,${encodeURIComponent(
                                                    coupon.coupons.map((c) => `"${c.code}"`).join('\n')
                                                )}`}
                                                className="font-medium text-indigo-600 hover:text-indigo-500"
                                            >
                                                {' '}
                                                Download{' '}
                                            </a>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
